import { useState, useEffect, useCallback, useMemo } from 'react';
import { Match, MatchFilters } from '@/lib/database';

interface UseMatchesResult {
  matches: Match[];
  total: number;
  page: number;
  totalPages: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  loadMore: () => void;
  hasMore: boolean;
}

interface UseMatchResult {
  match: Match | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

interface UseMatchCountsResult {
  counts: {
    total: number;
    live: number;
    today: number;
    tomorrow: number;
  } | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useMatches(filters: MatchFilters = {}): UseMatchesResult {
  const [matches, setMatches] = useState<Match[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(filters.page || 1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Memoize filters để tránh re-render không cần thiết
  const memoizedFilters = useMemo(() => filters, [
    filters.type,
    filters.limit,
    filters.status,
    filters.match_type,
    filters.league,
    filters.page
  ]);

  const fetchMatches = useCallback(async (currentPage = 1, append = false) => {
    try {
      setLoading(true);
      setError(null);

      const queryParams = new URLSearchParams();
      
      // Add filters to query params, converting all values to strings
      if (memoizedFilters.type) queryParams.set('type', memoizedFilters.type);
      if (memoizedFilters.limit) queryParams.set('limit', memoizedFilters.limit.toString());
      if (memoizedFilters.status) queryParams.set('status', memoizedFilters.status);
      if (memoizedFilters.match_type) queryParams.set('match_type', memoizedFilters.match_type);
      if (memoizedFilters.league) queryParams.set('league', memoizedFilters.league);
      queryParams.set('page', currentPage.toString());

      const response = await fetch(`/api/matches?${queryParams}`);
      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch matches');
      }

      const { matches: newMatches, total, page, totalPages } = result.data;

      if (append) {
        setMatches(prev => [...prev, ...newMatches]);
      } else {
        setMatches(newMatches);
      }
      
      setTotal(total);
      setPage(page);
      setTotalPages(totalPages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [memoizedFilters]);

  const refetch = useCallback(() => {
    setPage(1);
    fetchMatches(1, false);
  }, [fetchMatches]);

  const loadMore = useCallback(() => {
    if (page < totalPages) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchMatches(nextPage, true);
    }
  }, [page, totalPages, fetchMatches]);

  useEffect(() => {
    fetchMatches(1, false);
  }, [fetchMatches]);

  return {
    matches,
    total,
    page,
    totalPages,
    loading,
    error,
    refetch,
    loadMore,
    hasMore: page < totalPages
  };
}

export function useMatch(id: number): UseMatchResult {
  const [match, setMatch] = useState<Match | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMatch = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`/api/matches/${id}`);
      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch match');
      }

      setMatch(result.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const refetch = useCallback(() => {
    fetchMatch();
  }, [fetchMatch]);

  useEffect(() => {
    if (id) {
      fetchMatch();
    }
  }, [fetchMatch, id]);

  return {
    match,
    loading,
    error,
    refetch
  };
}

export function useMatchCounts(): UseMatchCountsResult {
  const [counts, setCounts] = useState<{
    total: number;
    live: number;
    today: number;
    tomorrow: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCounts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/matches/counts');
      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch match counts');
      }

      setCounts(result.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  const refetch = useCallback(() => {
    fetchCounts();
  }, [fetchCounts]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  return {
    counts,
    loading,
    error,
    refetch
  };
}

// Utility function to format match data for components
export function formatMatchForCard(match: Match) {
  // Format time similar to WordPress: date('H:i d/m/Y', strtotime($time_start))
  const formatMatchTime = (timeString: string) => {
    if (!timeString || timeString === '' || timeString === '0000-00-00 00:00:00') {
      return { time: '--:--', date: '--/--' };
    }
    
    try {
      let date: Date;
      
      // Try to parse different time formats
      if (timeString.includes('T')) {
        // ISO format: 2024-01-15T14:30:00
        date = new Date(timeString);
      } else if (timeString.includes('-') && timeString.includes(':')) {
        // MySQL datetime format: 2024-01-15 14:30:00
        date = new Date(timeString.replace(' ', 'T'));
      } else if (timeString.includes('/')) {
        // Date format: 15/01/2024 14:30
        const parts = timeString.split(' ');
        if (parts.length >= 2) {
          const datePart = parts[0]; // 15/01/2024
          const timePart = parts[1]; // 14:30
          const [day, month, year] = datePart.split('/');
          date = new Date(`${year}-${month}-${day}T${timePart}:00`);
        } else {
          date = new Date(timeString);
        }
      } else {
        // Try direct parsing
        date = new Date(timeString);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date format:', timeString);
        return { time: '--:--', date: '--/--' };
      }
      
      // Format time as HH:mm
      const time = date.toLocaleTimeString('vi-VN', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false,
        timeZone: 'Asia/Ho_Chi_Minh'
      });
      
      // Format date as dd/mm
      const dateStr = date.toLocaleDateString('vi-VN', { 
        day: '2-digit', 
        month: '2-digit',
        timeZone: 'Asia/Ho_Chi_Minh'
      });
      
      return { time, date: dateStr };
    } catch (error) {
      console.error('Error formatting time:', error, 'Input:', timeString);
      return { time: '--:--', date: '--/--' };
    }
  };

  const timeData = formatMatchTime(match.time_start || match.time || '');

  return {
    id: match.id,
    title: match.title,
    slug: match.slug,
    homeTeam: match.team_hone || '',
    awayTeam: match.team_away || '',
    homeScore: match.home_score || '',
    awayScore: match.away_score || '',
    status: match.match_status || '1',
    time: timeData.time,
    date: timeData.date,
    fullTime: match.time_start || match.time || '', // Keep original for other uses
    league: match.league_name || '',
    fire: parseInt(match.fire || '0'),
    views: parseInt(match.views || '0'),
    matchType: match.match_type || '1',
    isLive: match.match_status === '3',
    isFinished: match.match_status === '2'
  };
} 